import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, Server, Coins, Cpu, Eye, EyeOff, Lock, Unlock, ClipboardList, Database, Globe, ShoppingBag, MessageSquare, Megaphone, Radio, Calendar, Users, Briefcase, Sparkles, Building, Terminal, Search } from 'lucide-react';
import { NexusUser, WorkSuiteTask, SocialPost } from '../types/nexus';
import { NexusSearch } from './NexusSearch';
import { NexusHosting } from './NexusHosting';
import { NexusMarketplace } from './NexusMarketplace';
import { NexusWorkSuite } from './NexusWorkSuite';
import { NexusChat } from './NexusChat';
import { NexusMediaAdSocial } from './NexusMediaAdSocial';
import { NexusAgents } from './NexusAgents';
import { NexusMailDomains } from './NexusMailDomains';
import { NexusOrganicGrowthEngine } from './NexusOrganicGrowthEngine';
import { NexusGalaxyLab } from './NexusGalaxyLab';
import { NexusAdminCRM } from './NexusAdminCRM';
import { NexusDigitalProductMarketplace } from './NexusDigitalProductMarketplace';
import { ClientCampaignWorkspace } from './ClientCampaignWorkspace';

interface NexusControlPlaneProps {
  initialUser: NexusUser;
  mfaVerified: boolean;
  isDirectorUser: boolean;
}

export const NexusControlPlane: React.FC<NexusControlPlaneProps> = ({
  initialUser,
  mfaVerified,
  isDirectorUser
}) => {
  // Global Connected States
  const [user, setUser] = useState<NexusUser>({
    email: initialUser?.email || 'unregistered-client@nexorax.co.uk',
    displayName: initialUser?.displayName || 'NexoraX Client Representative',
    plan: 'Bronze',
    workspaceId: 'ns-7729',
    searchCredits: 500,
    aiTasksRemaining: 50,
    rendersRemaining: 20,
    hostingStatus: 'Active'
  });

  const [isAdminMode, setIsAdminMode] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<
    'Overview' | 'Search' | 'Hosting' | 'Marketplace' | 'WorkSuite' | 'Chat' | 'MediaStudio' | 'Agents' | 'AdminCRM' | 'MailDomains' | 'OrganicGrowth' | 'GalaxyLab' | 'DigitalProducts' | 'CampaignAssets'
  >('Overview');

  useEffect(() => {
    const checkInitialSubtab = () => {
      const sub = sessionStorage.getItem('nexus_os_initial_subtab');
      if (sub === 'MailDomains') {
        setActiveSubTab('MailDomains');
        sessionStorage.removeItem('nexus_os_initial_subtab');
      } else if (sub === 'OrganicGrowth') {
        setActiveSubTab('OrganicGrowth');
        sessionStorage.removeItem('nexus_os_initial_subtab');
      } else if (sub === 'GalaxyLab') {
        setActiveSubTab('GalaxyLab');
        sessionStorage.removeItem('nexus_os_initial_subtab');
      } else if (sub === 'CampaignAssets') {
        setActiveSubTab('CampaignAssets');
        sessionStorage.removeItem('nexus_os_initial_subtab');
      }
    };
    checkInitialSubtab();
    // Also listen for landing tab changes
    window.addEventListener('nexorax-landing-tab-changed', checkInitialSubtab);
    return () => {
      window.removeEventListener('nexorax-landing-tab-changed', checkInitialSubtab);
    };
  }, []);

  const [showBillingModal, setShowBillingModal] = useState(false);

  // Unified data lists shared across segments
  const [tasks, setTasks] = useState<WorkSuiteTask[]>([
    { id: 't-1', title: 'VERIFY CLOUD ROUTING AND HOSTING INSTANCE BASELINES', project: 'General Operations', client: 'Enterprise Support Desk', priority: 'HIGH', status: 'TODO' },
    { id: 't-2', title: 'ALIGN PORTFOLIO INTEGRATION PIPELINES', project: 'Financial Logistics', client: 'Global Client Advisory', priority: 'URGENT', status: 'TODO' },
    { id: 't-3', title: 'AUDIT ENTERPRISE SECURITY AND CLIENT INTERACTIVE PROTOCOLS', project: 'Security Gateways', client: 'Internal AdminDesk', priority: 'MEDIUM', status: 'COMPLETED' }
  ]);

  const [posts, setPosts] = useState<SocialPost[]>([
    { id: 's-1', platform: 'LinkedIn', caption: 'Thrilled to launch the unified Nexus OS Ecosystem. Complete control in one place.', status: 'Approved', scheduledTime: 'In 2 hours' },
    { id: 's-2', platform: 'Twitter', caption: 'Legacy multi-tenant SaaS hosting is in "the bin". Long live NexoraX server layers.', status: 'Queued', scheduledTime: 'In 5 hours' }
  ]);

  const [auditLogs, setAuditLogs] = useState<Array<{
    id: string;
    timestamp: string;
    module: string;
    action: string;
    status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL';
    details: string;
  }>>([
    { id: 'log-1', timestamp: new Date().toLocaleTimeString(), module: 'Control Plane', action: 'Initialize Nexus OS', status: 'SUCCESS', details: 'Core system variables synched and authenticated.' }
  ]);

  // Lead interest CRM state
  const [crmLeads, setCrmLeads] = useState<Array<{ id: string, company: string, email: string, plan: string, status: string }>>([
    { id: 'ld-1', company: 'Global Tech Sol', email: 'director@globaltech.com', plan: 'Platinum', status: 'Active Thread' },
    { id: 'ld-2', company: 'Finances Inc', email: 'officer@financesinc.co.uk', plan: 'Silver', status: 'Awaiting Demo' }
  ]);

  const addAuditLog = (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => {
    setAuditLogs(prev => [
      {
        id: `log-${Date.now()}`,
        timestamp: new Date().toLocaleTimeString(),
        module,
        action,
        status,
        details
      },
      ...prev
    ]);
  };

  const handleUpdatePlan = (selectedPlan: 'Bronze' | 'Silver' | 'Gold' | 'Platinum') => {
    setUser(prev => ({ ...prev, plan: selectedPlan }));
    addAuditLog('Control Plane', 'Upgrade plan entitlement', 'SUCCESS', `Workspace subscription status updated to premium "${selectedPlan}".`);
  };

  const handlePurchaseCredits = (credits: number, cost: number) => {
    setUser(prev => ({
      ...prev,
      searchCredits: prev.searchCredits + credits
    }));
    addAuditLog('Control Plane', 'Purchase credits pack', 'SUCCESS', `Secured ${credits} Search Credits via Stripe portal. Value: £${cost.toFixed(2)}.`);
  };

  const addTaskFromOutside = (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => {
    const newTask: WorkSuiteTask = {
      id: `task-${Date.now()}`,
      title,
      project: 'NexoraX Mail Ingress',
      client: 'Office of Director',
      priority,
      status: 'TODO'
    };
    setTasks(prev => [newTask, ...prev]);
  };

  // Switch billing logic / labels
  const handleUpgradeInteraction = (planName: 'Bronze' | 'Silver' | 'Gold' | 'Platinum', price: number) => {
    addAuditLog('Billing Hub', `Initiate subscription checkout: ${planName}`, 'PENDING_APPROVAL', 'Invoked Secure Checkout webhook for subscription upgrade.');
    const consent = window.confirm(`SECURE CHECKOUT\n\nUpgrade Plan to: ${planName}\nMonthly Recurrent: £${price}/mo\n\nAuthorize recurring subscription charges via Connected Card Provider?`);
    if (consent) {
      handleUpdatePlan(planName);
      addAuditLog('Billing Hub', `Checkout success: ${planName}`, 'SUCCESS', `Subscription fully active. Workspace limits re-rationed.`);
      alert(`SUCCESS!\n\nYour Nexus OS workspace has been configured with premium [${planName}] entitlements.`);
    } else {
      addAuditLog('Billing Hub', `Checkout aborted: ${planName}`, 'WARNING', 'Payments portal closed by system supervisor.');
    }
  };

  return (
    <div className="w-full space-y-6 text-left relative selection:bg-[#00ffff]/30 selection:text-white">
      {/* HEADER BAR FOR CONTROL LAYER */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center p-5 bg-[#030612]/90 border border-zinc-800 rounded-2xl gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-purple-900 to-cyan-950 border border-[#00ffff]/20 rounded-xl animate-pulse">
            <Radio className="w-6 h-6 text-[#00ffff]" />
          </div>
          <div>
            <h1 className="text-xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-1.5 leading-none">
              Nexus Control Plane
              <span className="text-[10px] font-mono font-bold text-[#00ffff] bg-[#00ffff]/10 border border-[#00ffff]/20 px-2 py-0.5 rounded">
                NEURAL-V.09.2
              </span>
            </h1>
            <p className="text-[10.5px] font-mono text-slate-450 uppercase mt-1">
              CONNECTED WORKSPACE AUTOMATION CORE • ID: <strong className="text-white font-extrabold">{user.workspaceId}</strong>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs font-mono">
          {/* Admin Override Switch for Director - ONLY VISIBLE TO SUPER ADMIN / DIRECTOR */}
          {isDirectorUser && (
            <div className="flex items-center gap-2 bg-black/50 border border-zinc-850 px-3 py-1.5 rounded-xl text-[10px] uppercase font-bold">
              <span className="text-slate-500">Admin Control Hub:</span>
              <button
                onClick={() => {
                  setIsAdminMode(!isAdminMode);
                  addAuditLog('Admin Control Hub', isAdminMode ? 'Deactivate console' : 'Elevate Console Override', 'SUCCESS', 'Toggled root engineering details panel.');
                }}
                className={`px-2 py-0.5 rounded text-[8.5px] font-extrabold uppercase transition-all cursor-pointer flex items-center gap-1 ${
                  isAdminMode
                    ? 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
                    : 'bg-zinc-800 text-slate-400 hover:text-white'
                }`}
              >
                {isAdminMode ? <Terminal size={11} className="animate-pulse" /> : null}
                {isAdminMode ? 'ACTIVE (OVERRIDE)' : 'DISABLED'}
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 bg-black/50 border border-zinc-850 px-3 py-1.5 rounded-xl select-none">
            <span className="text-slate-500 uppercase text-[10px]">AUTH ID:</span>
            <span className="text-[#00ffff] font-extrabold text-[10px] truncate max-w-[120px]">{user.email}</span>
          </div>
        </div>
      </div>

      {/* METRICS DISPATCH TELEMETRY BANNER (METERING VISUALS) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Search credits meter */}
        <div className="glass-panel border border-zinc-850 p-4 rounded-xl bg-black/30 space-y-2 relative group hover:border-[#00ffff]/20 transition-all">
          <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 uppercase">
            <span>Search Credits</span>
            <Coins size={11} className="text-cyan-400" />
          </div>
          <p className="text-lg font-bold font-sans text-white tracking-tight">
            {user.searchCredits} <span className="text-[11px] font-mono text-slate-500 font-light">queries</span>
          </p>
          <div className="text-[8.5px] font-mono text-[#00ffff] uppercase hover:underline cursor-pointer" onClick={() => setActiveSubTab('Marketplace')}>
            + Buy Credit Pack from 1p/ea
          </div>
        </div>

        {/* AI task meter */}
        <div className="glass-panel border border-zinc-850 p-4 rounded-xl bg-black/30 space-y-2 relative group hover:border-zinc-800 transition-all">
          <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 uppercase">
            <span>AI Agent Runs</span>
            <Cpu size={11} className="text-[#e840f3]" />
          </div>
          <p className="text-lg font-bold font-sans text-white tracking-tight">
            {user.aiTasksRemaining} <span className="text-[11px] font-mono text-slate-500 font-light">remaining</span>
          </p>
          <span className="text-[8.5px] font-mono text-slate-500 block uppercase">
            Allocated Plan balance
          </span>
        </div>

        {/* GPU studio renders */}
        <div className="glass-panel border border-zinc-850 p-4 rounded-xl bg-black/30 space-y-2 relative group hover:border-zinc-800 transition-all">
          <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 uppercase">
            <span>GPU Ad Assets Renders</span>
            <Sparkles size={11} className="text-purple-400" />
          </div>
          <p className="text-lg font-bold font-sans text-white tracking-tight">
            {user.rendersRemaining} <span className="text-[11px] font-mono text-slate-500 font-light">exports</span>
          </p>
          <span className="text-[8.5px] font-mono text-slate-500 block uppercase">
            H100 Cluster Priority
          </span>
        </div>

        {/* Plan type */}
        <div className="glass-panel border border-[#e840f3]/20 p-4 rounded-xl bg-black/30 space-y-2 relative group hover:border-[#e840f3]/40 transition-all">
          <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 uppercase">
            <span>Entitled Plan</span>
            <ShieldCheck size={11} className="text-emerald-400" />
          </div>
          <p className="text-lg font-bold font-sans text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-[#e840f3] to-cyan-400 tracking-tight">
            {user.plan} <span className="text-[9.5px] font-mono text-slate-500 font-light uppercase">Premium</span>
          </p>
          <div className="text-[8.5px] font-mono text-[#e840f3] uppercase hover:underline cursor-pointer" onClick={() => setShowBillingModal(true)}>
            ⚙ Manage Subscription Billing
          </div>
        </div>
      </div>

      {/* CORE HUB INTERACTIVE NAVIGATION TABS */}
      <div className="flex flex-wrap gap-2 border-b border-zinc-900 pb-3 text-xs font-mono font-black uppercase">
        <button
          onClick={() => setActiveSubTab('Overview')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'Overview' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          Workspace Overview
        </button>
        <button
          onClick={() => setActiveSubTab('Search')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'Search' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          Nexus Search
        </button>
        <button
          onClick={() => setActiveSubTab('Hosting')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'Hosting' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          Nexus Hosting
        </button>
        <button
          onClick={() => setActiveSubTab('MailDomains')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'MailDomains' ? 'bg-[#e840f3]/15 text-[#e840f3] border border-[#e840f3]/35 shadow-[0_0_15px_rgba(232,64,243,0.15)]' : 'text-slate-500'
          }`}
        >
          Nexus Mail & Domains
        </button>
        <button
          onClick={() => setActiveSubTab('OrganicGrowth')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'OrganicGrowth' ? 'bg-[#ffaa00]/15 text-[#ffaa00] border border-[#ffaa00]/35 shadow-[0_0_15px_rgba(255,170,0,0.15)]' : 'text-slate-500'
          }`}
        >
          Organic Growth Engine
        </button>
        <button
          onClick={() => setActiveSubTab('GalaxyLab')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'GalaxyLab' ? 'bg-purple-950/40 text-purple-300 border border-purple-500/35 shadow-[0_0_15px_rgba(168,85,247,0.15)]' : 'text-slate-500'
          }`}
        >
          🌌 Galaxy Lab
        </button>
        <button
          onClick={() => setActiveSubTab('Marketplace')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'Marketplace' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          Marketplace Add-ons
        </button>
        <button
          onClick={() => setActiveSubTab('DigitalProducts')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'DigitalProducts' ? 'bg-purple-950/40 text-[#e840f3] border border-[#e840f3]/35 shadow-[0_0_15px_rgba(232,64,243,0.15)]' : 'text-slate-500'
          }`}
        >
          🎁 Digital Products
        </button>
        <button
          onClick={() => setActiveSubTab('WorkSuite')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'WorkSuite' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          WorkSuite PM
        </button>
        <button
          onClick={() => setActiveSubTab('Chat')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'Chat' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          NexoraX Chat
        </button>
        <button
          onClick={() => setActiveSubTab('MediaStudio')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'MediaStudio' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          Media & Social Scheduler
        </button>
        <button
          onClick={() => setActiveSubTab('CampaignAssets')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'CampaignAssets' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          ★ Campaign Assets
        </button>
        <button
          onClick={() => setActiveSubTab('Agents')}
          className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-zinc-900/40 transition-all ${
            activeSubTab === 'Agents' ? 'bg-cyan-950/40 text-[#00ffff] border border-cyan-500/20' : 'text-slate-500'
          }`}
        >
          Autonomous Agents
        </button>
        {isAdminMode && (
          <button
            onClick={() => setActiveSubTab('AdminCRM')}
            className={`py-1.5 px-3 rounded-xl cursor-pointer hover:bg-red-950/45 text-red-400 border border-red-500/25 shadow-[0_0_15px_rgba(239,68,68,0.1)] transition-all`}
          >
            🛡️ Admin Control Hub
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {/* VIEW 1: CENTRAL OVERVIEW / DASHBOARD */}
        {activeSubTab === 'Overview' && (
          <motion.div
            key="workspace-overview"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Left 2 col dashboard blocks */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Launch Panel */}
              <div className="glass-panel border border-[#00ffff]/10 p-6 rounded-2xl bg-gradient-to-br from-[#0c0f1d] via-[#05060d] to-black relative">
                <h2 className="text-sm font-mono font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-3 mb-4">
                  Autonomous Operations Center
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5 text-center text-xs font-mono uppercase font-black tracking-wide">
                  <div
                    onClick={() => setActiveSubTab('Search')}
                    className="p-4 bg-black/60 border border-zinc-850 hover:border-[#00ffff]/30 rounded-xl cursor-pointer transition-all hover:bg-[#00ffff]/5 space-y-2 group"
                  >
                    <Search className="w-5 h-5 mx-auto text-cyan-400 group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] text-slate-300 block">Search Web</span>
                  </div>

                  <div
                    onClick={() => setActiveSubTab('Agents')}
                    className="p-4 bg-black/60 border border-zinc-850 hover:border-purple-500/30 rounded-xl cursor-pointer transition-all hover:bg-purple-900/5 space-y-2 group"
                  >
                    <Cpu className="w-5 h-5 mx-auto text-purple-400 group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] text-slate-300 block">Deploy Agent</span>
                  </div>

                  <div
                    onClick={() => setActiveSubTab('WorkSuite')}
                    className="p-4 bg-black/60 border border-zinc-850 hover:border-indigo-500/30 rounded-xl cursor-pointer transition-all hover:bg-indigo-950/10 space-y-2 group"
                  >
                    <ClipboardList className="w-5 h-5 mx-auto text-[#e840f3] group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] text-slate-300 block">Create Project</span>
                  </div>

                  <div
                    onClick={() => setActiveSubTab('MediaStudio')}
                    className="p-4 bg-black/60 border border-zinc-850 hover:border-[#e840f3]/30 rounded-xl cursor-pointer transition-all hover:bg-pink-950/10 space-y-2 group"
                  >
                    <Sparkles className="w-5 h-5 mx-auto text-pink-400 group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] text-slate-300 block">Generate Media</span>
                  </div>

                  <div
                    onClick={() => setActiveSubTab('MediaStudio')}
                    className="p-4 bg-black/60 border border-zinc-850 hover:border-cyan-455/30 rounded-xl cursor-pointer transition-all hover:bg-cyan-950/10 space-y-2 group"
                  >
                    <Calendar className="w-5 h-5 mx-auto text-[#00ffff] group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] text-slate-300 block">Schedule Post</span>
                  </div>

                  <div
                    onClick={() => {
                      addAuditLog('Support Center', 'Raise premium ticket link', 'SUCCESS', 'Outbound coordinate request routed to support@nexoraxdigital.co.uk.');
                      alert('DIRECT NEXORAX HELPDESK\n\nAdministrative ticket initialized. General support team notified on the 08000 622827 voice communications frequency.');
                    }}
                    className="p-4 bg-black/60 border border-zinc-850 hover:border-rose-455/30 rounded-xl cursor-pointer transition-all hover:bg-rose-950/10 space-y-2 group"
                  >
                    <Globe className="w-5 h-5 mx-auto text-rose-400 group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] text-rose-300 block">Contact Desk</span>
                  </div>
                </div>
              </div>

              {/* Connected Products Status lists */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Active Sub-agents list */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-xl bg-black/45 space-y-3 font-mono text-[10px]">
                  <h3 className="text-xs font-sans font-black text-white uppercase tracking-wider border-b border-zinc-900 pb-2">
                    Deployed Node Agents
                  </h3>
                  <div className="space-y-2 text-slate-400">
                    <div className="flex justify-between pb-1.5 border-b border-zinc-900/40">
                      <span>Vanguard Search Node-01:</span>
                      <span className="text-emerald-400 uppercase font-black text-[9px]">ONLINE (STANDBY)</span>
                    </div>
                    <div className="flex justify-between pb-1.5 border-b border-zinc-900/40">
                      <span>Lead Research Agent Node-06:</span>
                      <span className="text-emerald-400 uppercase font-black text-[9px]">ONLINE (STANDBY)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Social Media Automation Node-17:</span>
                      <span className="text-amber-500 uppercase font-black text-[9px] animate-pulse">EXECUTING LOGS</span>
                    </div>
                  </div>
                </div>

                {/* Infrastructure status */}
                <div className="glass-panel border border-zinc-850 p-5 rounded-xl bg-black/45 space-y-3 font-mono text-[10px]">
                  <h3 className="text-xs font-sans font-black text-white uppercase tracking-wider border-b border-zinc-900 pb-2">
                    Cloud Core Container status
                  </h3>
                  <div className="space-y-2 text-slate-400 text-[9.5px]">
                    <div className="flex justify-between">
                      <span>Server Location:</span>
                      <span className="text-white">German Location (Dedicated)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Let's Encrypt certificates:</span>
                      <span className="text-emerald-400">Secure (Active)</span>
                    </div>
                    <div className="flex justify-between font-bold">
                      <span>Uptime Meter:</span>
                      <span className="text-emerald-450">99.98% stable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Audit Logs Stream (1/3 col) */}
            <div className="space-y-6">
              {/* Chronos Ledger stream */}
              <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/35 space-y-4">
                <h3 className="text-xs font-mono font-black text-slate-350 uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-1.5">
                  <Database className="w-4 h-4 text-cyan-400" />
                  Chronos Ledger logs
                </h3>

                <div className="h-72 overflow-y-auto space-y-2.5 text-[9px] font-mono leading-relaxed custom-scrollbar text-zinc-400">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="p-2 bg-black/50 border border-zinc-900 rounded-lg space-y-1">
                      <div className="flex justify-between">
                        <span className="text-[#00ffff] font-extrabold text-[8px] uppercase">{log.module}</span>
                        <span className={`text-[7.5px] font-black uppercase px-1 rounded ${
                          log.status === 'SUCCESS' ? 'bg-emerald-950/40 text-emerald-400' :
                          log.status === 'WARNING' ? 'bg-amber-950/40 text-amber-500' :
                          log.status === 'DENIED' ? 'bg-rose-955/40 text-rose-400' :
                          'bg-cyan-950/40 text-cyan-400'
                        }`}>
                          {log.status}
                        </span>
                      </div>
                      <div className="text-white font-bold">{log.action.toUpperCase()}</div>
                      <p className="text-[8px] text-slate-500 normal-case">{log.details}</p>
                      <span className="text-[7px] text-slate-650 font-extrabold">{log.timestamp}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeSubTab === 'Search' && (
          <motion.div key="sub-search" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusSearch
              user={user}
              onAddLog={addAuditLog}
              onDeductCredits={(amt) => {
                setUser(prev => ({ ...prev, searchCredits: Math.max(0, prev.searchCredits - amt) }));
              }}
              isAdminMode={isAdminMode}
            />
          </motion.div>
        )}

        {activeSubTab === 'Hosting' && (
          <motion.div key="sub-hosting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusHosting
              user={user}
              isAdminMode={isAdminMode}
              onDeploy={() => {
                setUser(prev => ({ ...prev, hostingStatus: 'Active' }));
              }}
              onAddLog={addAuditLog}
            />
          </motion.div>
        )}

        {activeSubTab === 'MailDomains' && (
          <motion.div key="sub-maildomains" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusMailDomains
              user={user}
              isAdminMode={isAdminMode}
              onAddLog={addAuditLog}
              onAddTaskToWorkSuite={addTaskFromOutside}
              searchCredits={user.searchCredits}
              onAddSearchCredits={handlePurchaseCredits}
            />
          </motion.div>
        )}

        {activeSubTab === 'OrganicGrowth' && (
          <motion.div key="sub-organicgrowth" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusOrganicGrowthEngine
              user={user}
              isAdminMode={isAdminMode}
              onAddLog={addAuditLog}
              onAddTaskToWorkSuite={addTaskFromOutside}
              searchCredits={user.searchCredits}
              onAddSearchCredits={handlePurchaseCredits}
              currentRole={isAdminMode ? 'admin' : 'client'}
            />
          </motion.div>
        )}

        {activeSubTab === 'GalaxyLab' && (
          <motion.div key="sub-galaxylab" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusGalaxyLab
              user={user}
              isAdminMode={isAdminMode}
              onAddLog={addAuditLog}
              onAddTaskToWorkSuite={addTaskFromOutside}
              searchCredits={user.searchCredits}
              onAddSearchCredits={handlePurchaseCredits}
              currentRole={isAdminMode ? 'admin' : 'client'}
            />
          </motion.div>
        )}

        {activeSubTab === 'Marketplace' && (
          <motion.div key="sub-marketplace" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusMarketplace
              user={user}
              isAdmin={isAdminMode}
              onPurchaseCredits={handlePurchaseCredits}
              onUpgradePlan={handleUpdatePlan}
              onAddLog={addAuditLog}
              onAddTaskToWorkSuite={addTaskFromOutside}
            />
          </motion.div>
        )}

        {activeSubTab === 'DigitalProducts' && (
          <motion.div key="sub-digitalproducts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusDigitalProductMarketplace
              user={user}
              isAdmin={isAdminMode}
              onAddLog={addAuditLog}
              onAddTaskToWorkSuite={addTaskFromOutside}
            />
          </motion.div>
        )}

        {activeSubTab === 'WorkSuite' && (
          <motion.div key="sub-worksuite" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusWorkSuite
              user={user}
              onAddLog={addAuditLog}
              tasks={tasks}
              onTasksChanged={setTasks}
            />
          </motion.div>
        )}

        {activeSubTab === 'Chat' && (
          <motion.div key="sub-chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusChat
              user={user}
              onAddLog={addAuditLog}
              onAddTaskFromChat={(text) => {
                const newTask: WorkSuiteTask = {
                  id: `task-${Date.now()}`,
                  title: `CHAT MEMO: "${text.substring(0, 45).toUpperCase()}..."`,
                  project: 'Social Conversion',
                  client: 'Lobby Visitor',
                  priority: 'HIGH',
                  status: 'TODO'
                };
                setTasks([newTask, ...tasks]);
              }}
              onSearchFromChat={(query) => {
                setActiveSubTab('Search');
              }}
              onDeployAgent={(role) => {
                setActiveSubTab('Agents');
              }}
            />
          </motion.div>
        )}

        {activeSubTab === 'MediaStudio' && (
          <motion.div key="sub-mediastudio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusMediaAdSocial
              user={user}
              onAddLog={addAuditLog}
              onDeductRenders={(amt) => {
                setUser(prev => ({ ...prev, rendersRemaining: Math.max(0, prev.rendersRemaining - amt) }));
              }}
              posts={posts}
              onPostsChanged={setPosts}
            />
          </motion.div>
        )}

        {activeSubTab === 'CampaignAssets' && (
          <motion.div key="sub-campaignassets" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ClientCampaignWorkspace 
              triggerToast={(msg, type) => addAuditLog('Campaign Launcher', msg, type === 'success' ? 'SUCCESS' : 'WARNING', msg)} 
              userTier={user.plan}
              initialTab="assets"
            />
          </motion.div>
        )}

        {activeSubTab === 'Agents' && (
          <motion.div key="sub-agents" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusAgents
              user={user}
              onAddLog={addAuditLog}
              onDeductTasks={(amt) => {
                setUser(prev => ({ ...prev, aiTasksRemaining: Math.max(0, prev.aiTasksRemaining - amt) }));
              }}
            />
          </motion.div>
        )}

        {activeSubTab === 'AdminCRM' && (
          <motion.div key="sub-admincrm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <NexusAdminCRM
              user={user}
              onAddLog={addAuditLog}
              tasks={tasks}
              posts={posts}
              onTasksChanged={setTasks}
              onPostsChanged={setPosts}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* BILLING CONFIGURATION MODAL / SLIDE OVER */}
      {showBillingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm normal-case">
          <div className="glass-panel border border-cyan-555/20 max-w-xl w-full bg-[#030612] p-6 rounded-2xl space-y-6 relative">
            <button
              onClick={() => setShowBillingModal(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-white font-mono text-xs uppercase"
            >
              ✖ Close
            </button>

            <div className="text-center space-y-1">
              <h3 className="text-lg font-sans font-black text-white uppercase tracking-tight">
                Secure Account Billing Hub
              </h3>
              <p className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest">
                Tiered Enterprise Subscription Matrices
              </p>
            </div>

            <p className="text-xs text-slate-400 font-sans leading-relaxed text-center font-medium leading-normal max-w-md mx-auto">
              Select an institutional subscription tier. All secure online payments are routed through our verified PCI-compliant card gateway.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
              {/* Bronze Package */}
              <div className="p-4 border border-zinc-850 rounded-xl bg-black/30 space-y-2 flex flex-col justify-between">
                <div>
                  <h4 className="text-white font-black uppercase text-[11px] tracking-tight">Bronze Core</h4>
                  <p className="text-[10px] text-slate-500 leading-normal normal-case pt-1 font-medium">Standard search capabilities, Basic WorkSuite operations desk.</p>
                </div>
                <div className="pt-2">
                  <div className="text-white font-black text-sm pb-2">£19.00 / month</div>
                  <button
                    onClick={() => handleUpgradeInteraction('Bronze', 19)}
                    className="w-full py-2 bg-gradient-to-r from-purple-650 to-cyan-600 text-black font-black uppercase text-[9px] rounded-lg tracking-wide cursor-pointer"
                  >
                    Activate Tier
                  </button>
                </div>
              </div>

              {/* Silver Package */}
              <div className="p-4 border border-zinc-850 rounded-xl bg-black/30 space-y-2 flex flex-col justify-between">
                <div>
                  <h4 className="text-cyan-400 font-black uppercase text-[11px] tracking-tight">Silver Advanced</h4>
                  <p className="text-[10px] text-slate-500 leading-normal normal-case pt-1 font-medium">Unlocks premium client chatbot lobes, DNS setups, and basic automation pipelines.</p>
                </div>
                <div className="pt-2">
                  <div className="text-white font-black text-sm pb-2">£49.00 / month</div>
                  <button
                    onClick={() => handleUpgradeInteraction('Silver', 49)}
                    className="w-full py-2 bg-gradient-to-r from-purple-650 to-cyan-600 text-black font-black uppercase text-[9px] rounded-lg tracking-wide cursor-pointer"
                  >
                    Activate Tier
                  </button>
                </div>
              </div>

              {/* Gold Package */}
              <div className="p-4 border border-zinc-850 rounded-xl bg-black/30 space-y-2 flex flex-col justify-between">
                <div>
                  <h4 className="text-purple-400 font-black uppercase text-[11px] tracking-tight">Gold Pro automation</h4>
                  <p className="text-[10px] text-slate-500 leading-normal normal-case pt-1 font-medium">Unlocks complete autonomous Node Agent queues and full social scheduled publishers.</p>
                </div>
                <div className="pt-2">
                  <div className="text-white font-black text-sm pb-2">£99.00 / month</div>
                  <button
                    onClick={() => handleUpgradeInteraction('Gold', 99)}
                    className="w-full py-2 bg-gradient-to-r from-purple-650 to-[#e840f3] text-black font-black uppercase text-[9px] rounded-lg tracking-wide cursor-pointer"
                  >
                    Activate Tier
                  </button>
                </div>
              </div>

              {/* Platinum Package */}
              <div className="p-4 border border-[#e840f3]/25 rounded-xl bg-[#e840f3]/5 space-y-2 flex flex-col justify-between relative group">
                <div>
                  <h4 className="text-[#e840f3] font-black uppercase text-[11px] tracking-tight">Platinum NexoraX</h4>
                  <p className="text-[10px] text-zinc-400 leading-normal normal-case pt-1 font-semibold">Maximum capabilities. Infinite pipeline allocations, 24/7 dedicated H100 GPU priority slots.</p>
                </div>
                <div className="pt-2">
                  <div className="text-white font-black text-sm pb-2">£249.00 / month</div>
                  <button
                    onClick={() => handleUpgradeInteraction('Platinum', 249)}
                    className="w-full py-2 bg-[#e840f3] hover:opacity-90 text-black font-black uppercase text-[9px] rounded-lg tracking-wide cursor-pointer"
                  >
                    Activate Tier
                  </button>
                </div>
              </div>
            </div>

            <div className="text-center font-mono text-[9px] text-slate-500 uppercase border-t border-zinc-900 pt-3 flex justify-between items-center">
              <span>* 20% VAT accounting breakout generated on B2B invoices automatically</span>
              <button
                onClick={() => {
                  addAuditLog('Billing Hub', 'Request standard B2B invoice', 'PENDING_APPROVAL', 'Dispatched custom invoice request to admin inbox.');
                  alert('INVOICE DISPATCHED\nManual bank transfer invoice file queued for workspace ns-7729.');
                }}
                className="text-[#00ffff] hover:underline"
              >
                Req Bank Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
